import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSystemSignaturesRequest,
  fetchSystemSignaturesSuccess,
  fetchSystemSignaturesFailure,
  createSystemSignatureRequest,
  createSystemSignatureSuccess,
  createSystemSignatureFailure,
  updateSystemSignatureRequest,
  updateSystemSignatureSuccess,
  updateSystemSignatureFailure,
  deleteSystemSignatureRequest,
  deleteSystemSignatureSuccess,
  deleteSystemSignatureFailure,
  setDefaultSystemSignatureRequest,
  setDefaultSystemSignatureSuccess,
  setDefaultSystemSignatureFailure,
  clearSignatureErrors,
} from '../store/actions/signatures.store.actions';
import {
  getSystemSignatures,
  getSystemSignaturesLoading,
  getSystemSignaturesError,
  getDefaultSystemSignature,
  getActiveSystemSignatures,
  getSignatureOperationLoading,
  getSignatureError,
} from '../store/selectors/signatures.selectors';
import { signaturesApi } from '../api/signatures.api';
import type { SystemSignature } from '../store/reducers/signatures.store';
import type {
  CreateSystemSignatureRequest,
  UpdateSystemSignatureRequest,
} from '../api/signatures.api';
import useNotifications from '../_lib/notifications/useNotifications';

export const useSystemSignatures = () => {
  const dispatch = useDispatch();
  const notifications = useNotifications();

  // Selectors
  const systemSignatures = useSelector(getSystemSignatures);
  const loading = useSelector(getSystemSignaturesLoading);
  const error = useSelector(getSystemSignaturesError);
  const defaultSystemSignature = useSelector(getDefaultSystemSignature);
  const activeSystemSignatures = useSelector(getActiveSystemSignatures);
  const operationLoading = useSelector(getSignatureOperationLoading);
  const operationError = useSelector(getSignatureError);

  // Fetch system signatures
  const fetchSystemSignatures = useCallback(async () => {
    try {
      dispatch(fetchSystemSignaturesRequest());
      const response = await signaturesApi.getSystemSignatures();

      // Handle paginated response - extract signatures from content array
      let signaturesData: any[] = [];
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data)) {
          // Direct array response
          signaturesData = response.data;
        } else if (
          (response.data as any).content &&
          Array.isArray((response.data as any).content)
        ) {
          // Paginated response with content array
          signaturesData = (response.data as any).content;
        }
      }

      dispatch(fetchSystemSignaturesSuccess(signaturesData));
    } catch (err: unknown) {
      console.error('Fetch system signatures error:', err);
      const errorMessage =
        (err as any).response?.data?.error ||
        (err as any).message ||
        'Failed to fetch system signatures';
      dispatch(fetchSystemSignaturesFailure(errorMessage));
      notifications.error(errorMessage);
    }
  }, [dispatch, notifications]);

  // Create system signature
  const createSystemSignature = useCallback(
    async (signatureData: CreateSystemSignatureRequest) => {
      try {
        dispatch(createSystemSignatureRequest());
        const response = await signaturesApi.createSystemSignature({
          data: signatureData,
        });
        // Ensure the response data is valid
        if (response.data && typeof response.data === 'object') {
          dispatch(createSystemSignatureSuccess(response.data));
          notifications.success('Team signature created successfully');
          return response.data;
        } else {
          throw new Error('Invalid response data from server');
        }
      } catch (err: unknown) {
        const errorMessage =
          (err as any).response?.data?.error ||
          (err as any).message ||
          'Failed to create system signature';
        dispatch(createSystemSignatureFailure(errorMessage));
        notifications.error(errorMessage);
        throw err;
      }
    },
    [dispatch, notifications]
  );

  // Update system signature
  const updateSystemSignature = useCallback(
    async (id: string, signatureData: UpdateSystemSignatureRequest) => {
      try {
        dispatch(updateSystemSignatureRequest());
        const response = await signaturesApi.updateSystemSignature(id, {
          data: signatureData,
        });
        // Ensure the response data is valid
        if (response.data && typeof response.data === 'object') {
          dispatch(updateSystemSignatureSuccess(response.data));
          notifications.success('Team signature updated successfully');
          return response.data;
        } else {
          throw new Error('Invalid response data from server');
        }
      } catch (err: unknown) {
        const errorMessage =
          (err as any).response?.data?.error ||
          (err as any).message ||
          'Failed to update system signature';
        dispatch(updateSystemSignatureFailure(errorMessage));
        notifications.error(errorMessage);
        throw err;
      }
    },
    [dispatch, notifications]
  );

  // Delete system signature
  const deleteSystemSignature = useCallback(
    async (id: string) => {
      try {
        dispatch(deleteSystemSignatureRequest());
        await signaturesApi.deleteSystemSignature(id);
        dispatch(deleteSystemSignatureSuccess(id));
        notifications.success('Team signature deleted successfully');
      } catch (err: unknown) {
        const errorMessage =
          (err as any).response?.data?.error ||
          (err as any).message ||
          'Failed to delete system signature';
        dispatch(deleteSystemSignatureFailure(errorMessage));
        notifications.error(errorMessage);
        throw err;
      }
    },
    [dispatch, notifications]
  );

  // Set default system signature
  const setDefaultSystemSignature = useCallback(
    async (id: string) => {
      try {
        dispatch(setDefaultSystemSignatureRequest());
        await signaturesApi.setDefaultSystemSignature(id);
        dispatch(setDefaultSystemSignatureSuccess(id));
        notifications.success('Default team signature updated');
      } catch (err: unknown) {
        const errorMessage =
          (err as any).response?.data?.error ||
          (err as any).message ||
          'Failed to set default system signature';
        dispatch(setDefaultSystemSignatureFailure(errorMessage));
        notifications.error(errorMessage);
        throw err;
      }
    },
    [dispatch, notifications]
  );

  // Clear errors
  const clearErrors = useCallback(() => {
    dispatch(clearSignatureErrors());
  }, [dispatch]);

  // Find system signature by ID
  const findSystemSignatureById = useCallback(
    (id: string): SystemSignature | undefined => {
      return Array.isArray(systemSignatures)
        ? systemSignatures.find((sig) => sig.id === id)
        : undefined;
    },
    [systemSignatures]
  );

  // Get system signature content by ID
  const getSystemSignatureContent = useCallback(
    (id: string): string => {
      const signature = findSystemSignatureById(id);
      return signature?.content || '';
    },
    [findSystemSignatureById]
  );

  // Auto-fetch system signatures on mount
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (
      !hasInitialized.current &&
      (!Array.isArray(systemSignatures) || systemSignatures.length === 0) &&
      !loading &&
      !error
    ) {
      hasInitialized.current = true;
      fetchSystemSignatures();
    }
  }, [systemSignatures, loading, error]);

  return {
    // State
    systemSignatures: Array.isArray(systemSignatures) ? systemSignatures : [],
    loading,
    error,
    defaultSystemSignature,
    activeSystemSignatures,
    operationLoading,
    operationError,

    // Actions
    fetchSystemSignatures,
    createSystemSignature,
    updateSystemSignature,
    deleteSystemSignature,
    setDefaultSystemSignature,
    clearErrors,

    // Utilities
    findSystemSignatureById,
    getSystemSignatureContent,
  };
};
